<template>
	<span class="c-culture">
		<a @click="setPopupMode('language')" class="c-culture__toggle">
			<DsIcon icon-name="globe" size="xs" />
			<span class="c-culture__toggle-text">
				{{ vm.BaseViewModel.CurrentCountryName }}
			</span>
		</a>

		<DsModal
			v-if="confirmCountryViewModel"
			@close-modal="setPopupMode('none')"
			:close-label="vm.BaseViewModel.CloseModalLabel ?? 'Close'"
			:is-open="show === 'country'"
			overflow="container"
			size="md"
		>
			<CountrySwitcher
				:country-label-text="confirmCountryViewModel.CountryLabelText"
				:country-list="confirmCountryViewModel.CountryList"
				:description="confirmCountryViewModel.Description"
				:headline="confirmCountryViewModel.Headline"
				:select-country-label-text="confirmCountryViewModel.SelectCountryLabelText ?? confirmCountryViewModel.CountryLabelText"
				@update:selected-country="setSelectedCountry"
			/>

			<template #actions>
				<DsButton
					variant="primary"
					class="c-culture__submit"
					@click="submitLanguage"
				>
					{{ confirmCountryViewModel.ContinueButtonText ?? 'Continue' }}
				</DsButton>
			</template>
		</DsModal>

		<DsModal
			@close-modal="setPopupMode('none')"
			:close-label="vm.BaseViewModel.CloseModalLabel ?? 'Close'"
			:is-open="show === 'language'"
			overflow="container"
			size="md"
		>
			<CountrySites
				:all-tab-enabled="vm.BaseViewModel.AllTabEnabled"
				:all-tab-label="vm.BaseViewModel.AllTabLabel"
				:country-list="vm.BaseViewModel.CountryList ?? []"
				:country-region-list="vm.BaseViewModel.CountryRegionList ?? []"
				:current-country-name="vm.BaseViewModel.CurrentCountryName"
				:headline="vm.BaseViewModel.Headline"
				:lang="lang"
				:lang-url="langUrl"
				:set-popup-mode="setPopupMode"
				:show="show"
			/>
		</DsModal>
	</span>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { DsButton, DsIcon, DsModal } from '@coloplast/design-system';
import CountrySwitcher from '../../CountrySwitcher/Views/CountrySwitcher.vue';
import CountrySites from './CountrySites.vue';
import { useCultureNavigation } from 'shared/composables/useCultureNavigation';
import { type Country } from '../../CountrySwitcher/Views/CountrySwitcher.types';
import { type Vm, type Vmc } from './types';

type ConfirmCountryViewModel = {
	CountryList: Country[];
	Description?: string;
	ContinueButtonText?: string;
	CountryLabelText?: string;
	SelectCountryLabelText?: string;
	Headline?: string;
};

const props = defineProps<{
	vm: Vm;
	vmc?: Vmc;
}>();

const show = ref<string>('none');
const lang = ref<string>(props.vm.BaseViewModel.lang ?? '');
const langUrl = ref<string>(props.vm.BaseViewModel.langUrl ?? '');
const selectedCountry = ref<Country | null>(null);

const { setCultureCookie } = useCultureNavigation();

const confirmCountryViewModel = computed<ConfirmCountryViewModel | null>(() => {
	return props.vmc?.BaseViewModel ?? null;
});

const isSelectedCountryCurrentCountry = computed(() => {
	return (
		selectedCountry.value?.IsSelected === true ||
		selectedCountry.value?.Name === props.vm.BaseViewModel.CurrentCountryName
	);
});

const setPopupMode = (name: string) => {
	show.value = name;
};

const setSelectedCountry = (country: Country) => {
	selectedCountry.value = country;
	lang.value = country.Name;
	langUrl.value = country.Url;
};

const submitLanguage = () => {
	if (!selectedCountry.value) {
		return;
	}

	setCultureCookie(selectedCountry.value.Name);
	setPopupMode('none');

	if (isSelectedCountryCurrentCountry.value) {
		return;
	}

	globalThis.location.assign(selectedCountry.value.Url);
};

const setCultureSelectorSwitcher = () => {
	const selectors = document.querySelectorAll('[data-culture-selector-item]');

	selectors.forEach((selector) => {
		const selectorInner = selector.querySelector(
			'span.c-nav-service__text'
		);

		if (selectorInner && props.vm.BaseViewModel.CurrentCountryName.length) {
			selectorInner.textContent =
				props.vm.BaseViewModel.CurrentCountryName;
		}

		selector.addEventListener('click', (event) => {
			event.preventDefault();
			setPopupMode('language');
		});
	});
};

onMounted(() => {
	const selected = confirmCountryViewModel.value?.CountryList.find(
		(country) => country.IsSelected
	);

	if (selected) {
		setSelectedCountry(selected);
		setPopupMode('country');
	}

	setCultureSelectorSwitcher();
});
</script>
